// src/hooks/useAdminQueries.ts
// ─────────────────────────────────────────────────────────────
// Hooks centralizados para o painel admin.
// Cache automático via React Query — sem refetch ao trocar aba.
// ─────────────────────────────────────────────────────────────

import { useQuery, useQueryClient } from '@tanstack/react-query';

const token = () => localStorage.getItem('accessToken') ?? '';
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

async function apiFetch<T>(url: string): Promise<T> {
    const res = await fetch(url, { headers: authHeader() });
    if (!res.ok) throw new Error(`Fetch error: ${url}`);
    return res.json();
}

// ─── QUERY KEYS (centralizados para invalidação precisa) ──────

export const QK = {
    // Platform
    platformConfig: ['platformConfig'] as const,
    platformContentStats: ['platformContentStats'] as const,

    // Users
    adminUsers: (params: Record<string, string>) => ['adminUsers', params] as const,

    // Subscriptions
    subscriptions: (search: string) => ['subscriptions', search] as const,
    subscriptionDecks: ['subscriptionDecks'] as const,

    // Sales – Overview
    overviewStats: ['overviewStats'] as const,
    overviewPerformance: (period: string) => ['overviewPerformance', period] as const,
    salesChart: (period: string) => ['salesChart', period] as const,
    yearChart: (year: number, semester: string) => ['yearChart', year, semester] as const,

    // Sales – Products
    products: ['products'] as const,
    decks: ['decks'] as const,
    cycles: ['cycles'] as const,

    // Sales – History
    salesHistory: (params: Record<string, string>) => ['salesHistory', params] as const,
    vendors: ['vendors'] as const,

    // Sales – Profitability
    profitabilityStats: (start: string, end: string) => ['profitabilityStats', start, end] as const,
    profitabilityExpenses: (type: string, start: string, end: string) => ['profitabilityExpenses', type, start, end] as const,
    profitabilityChart: (period: string) => ['profitabilityChart', period] as const,

    // Sales – Vendors
    vendorsList: (status: string) => ['vendorsList', status] as const,
    vendorRanking: (mode: string, period: string) => ['vendorRanking', mode, period] as const,
    vendorGoalRanking: (month: number | null, year: number | null) => ['vendorGoalRanking', month, year] as const,
    vendorAvailableMonths: ['vendorAvailableMonths'] as const,
    vendorPayments: (vendorId: string) => ['vendorPayments', vendorId] as const,

    // Revenue
    revenueByCategory: (period: string) => ['revenueByCategory', period] as const,

    // Accounts & Coupons
    accounts: ['accounts'] as const,
    myCoupons: ['myCoupons'] as const,
    myCouponsStats: ['myCouponsStats'] as const,
    allCoupons: ['allCoupons'] as const,
    couponsImpact: (period: string) => ['couponsImpact', period] as const,
    marketplaceProducts: ['marketplaceProducts'] as const,

    // Engagement
    engagementStats: ['engagementStats'] as const,
    engagementUsers: (page: number, limit: number) => ['engagementUsers', page, limit] as const,
} as const;

// ─────────────────────────────────────────────────────────────
// PLATFORM CONFIG
// ─────────────────────────────────────────────────────────────

export function usePlatformConfig() {
    return useQuery({
        queryKey: QK.platformConfig,
        queryFn: () => apiFetch<{
            interdictedTabs: Record<string, boolean>;
            tabsConfig: Record<string, { hidden: boolean; interdicted: boolean; allowedUsers: string[] }>;
        }>('/api/platform-config'),
        staleTime: 1000 * 60 * 5,
    });
}

// ─────────────────────────────────────────────────────────────
// PLATFORM CONTENT STATS (AdminStats — aba GRAIA)
// ─────────────────────────────────────────────────────────────

export function usePlatformContentStats(enabled = true) {
    return useQuery({
        queryKey: QK.platformContentStats,
        queryFn: () => apiFetch<{ content: { totalDecks: number; totalSubdecks: number; totalCards: number; totalApostilas: number; totalQuestions: number } }>('/api/admin/system/stats'),
        enabled,
        staleTime: 1000 * 60 * 10,
    });
}

// ─────────────────────────────────────────────────────────────
// ADMIN USERS
// ─────────────────────────────────────────────────────────────

export function useAdminUsers(params: {
    search: string;
    university: string;
    course: string;
    semester: string;
    authProvider: string;
}) {
    const p: Record<string, string> = {};
    if (params.search) p.search = params.search;
    if (params.university) p.university = params.university;
    if (params.course) p.course = params.course;
    if (params.semester) p.semester = params.semester;
    if (params.authProvider) p.authProvider = params.authProvider;

    return useQuery({
        queryKey: QK.adminUsers(p),
        queryFn: () => apiFetch<{ users: any[]; filters: any }>(`/api/admin/users?${new URLSearchParams(p)}`),
    });
}

// ─────────────────────────────────────────────────────────────
// SUBSCRIPTIONS
// ─────────────────────────────────────────────────────────────

export function useSubscriptions(search: string) {
    const param = search ? `?search=${encodeURIComponent(search)}` : '';
    return useQuery({
        queryKey: QK.subscriptions(search),
        queryFn: () => apiFetch<{ users: any[]; stats: any }>(`/api/admin/sales/all-users-subscriptions${param}`),
    });
}

export function useSubscriptionDecks() {
    return useQuery({
        queryKey: QK.subscriptionDecks,
        queryFn: () => apiFetch<{ decks: any[] }>('/api/admin/decks/all'),
        staleTime: 1000 * 60 * 10,
    });
}

// ─────────────────────────────────────────────────────────────
// OVERVIEW TAB
// ─────────────────────────────────────────────────────────────

export function useOverviewStats() {
    return useQuery({
        queryKey: QK.overviewStats,
        queryFn: () => apiFetch<any>('/api/admin/sales/overview-stats'),
    });
}

export function useOverviewPerformance(period: string) {
    return useQuery({
        queryKey: QK.overviewPerformance(period),
        queryFn: () => apiFetch<any>(`/api/admin/analytics/products/performance?period=${period}`),
    });
}

export function useSalesChart(period: string) {
    return useQuery({
        queryKey: QK.salesChart(period),
        queryFn: () => apiFetch<{ chartData: any[] }>(`/api/admin/sales/sales-chart?period=${period}`),
    });
}

export function useYearChart(year: number, semester: string) {
    const param = semester !== 'all' ? `&semester=${semester}` : '';
    return useQuery({
        queryKey: QK.yearChart(year, semester),
        queryFn: () => apiFetch<{ chartData: any[] }>(`/api/admin/analytics/sales-by-year?year=${year}${param}`),
    });
}

// ─────────────────────────────────────────────────────────────
// PRODUCTS MANAGER
// ─────────────────────────────────────────────────────────────

export function useAdminProducts() {
    return useQuery({
        queryKey: QK.products,
        queryFn: () => apiFetch<{ products: any[] }>('/api/admin/marketplace/products'),
        staleTime: 1000 * 60 * 5,
    });
}

export function useAdminDecks() {
    return useQuery({
        queryKey: QK.decks,
        queryFn: () => apiFetch<{ decks: any[] }>('/api/admin/marketplace/decks/all'),
        staleTime: 1000 * 60 * 10,
    });
}

export function useAdminCycles() {
    return useQuery({
        queryKey: QK.cycles,
        queryFn: () => apiFetch<any[]>('/api/cycles'),
        staleTime: 1000 * 60 * 10,
    });
}

// ─────────────────────────────────────────────────────────────
// SALES HISTORY TAB
// ─────────────────────────────────────────────────────────────

export function useSalesHistory(params: {
    status: string;
    period: string;
    vendorId: string;
    startDate: string;
    endDate: string;
}) {
    const p: Record<string, string> = {};
    if (params.status !== 'all') p.status = params.status;
    if (params.period !== 'all') p.period = params.period;
    if (params.vendorId !== 'all') p.vendorId = params.vendorId;
    if (params.startDate) p.startDate = params.startDate;
    if (params.endDate) p.endDate = params.endDate;

    return useQuery({
        queryKey: QK.salesHistory(p),
        queryFn: () => apiFetch<{ sales: any[]; stats: any }>(`/api/admin/sales/sales-history?${new URLSearchParams(p)}`),
    });
}

export function useSalesVendors() {
    return useQuery({
        queryKey: QK.vendors,
        queryFn: () => apiFetch<{ vendors: any[] }>('/api/admin/sales/vendors'),
        staleTime: 1000 * 60 * 5,
    });
}

export function useSalesProducts() {
    return useQuery({
        queryKey: QK.marketplaceProducts,
        queryFn: () => apiFetch<{ products: any[] }>('/api/admin/marketplace/products'),
        staleTime: 1000 * 60 * 5,
    });
}

// ─────────────────────────────────────────────────────────────
// PROFITABILITY TAB
// ─────────────────────────────────────────────────────────────

export function useProfitabilityStats(startDate: string, endDate: string) {
    const p = new URLSearchParams();
    if (startDate) p.append('startDate', startDate);
    if (endDate) p.append('endDate', endDate);

    return useQuery({
        queryKey: QK.profitabilityStats(startDate, endDate),
        queryFn: () => apiFetch<any>(`/api/admin/sales/profitability/stats?${p}`),
    });
}

export function useProfitabilityExpenses(filterType: string, startDate: string, endDate: string) {
    const p = new URLSearchParams();
    if (startDate) p.append('startDate', startDate);
    if (endDate) p.append('endDate', endDate);
    if (filterType !== 'all') p.append('type', filterType);

    return useQuery({
        queryKey: QK.profitabilityExpenses(filterType, startDate, endDate),
        queryFn: () => apiFetch<{ expenses: any[] }>(`/api/admin/sales/profitability/expenses?${p}`),
    });
}

export function useProfitabilityChart(period: string) {
    return useQuery({
        queryKey: QK.profitabilityChart(period),
        queryFn: () => apiFetch<{ chartData: any[] }>(`/api/admin/sales/profitability/chart?period=${period}`),
    });
}

// ─────────────────────────────────────────────────────────────
// VENDORS TAB
// ─────────────────────────────────────────────────────────────

export function useVendorsList(status: string) {
    const param = status !== 'all' ? `?status=${status}` : '';
    return useQuery({
        queryKey: QK.vendorsList(status),
        queryFn: () => apiFetch<{ vendors: any[] }>(`/api/admin/sales/vendors${param}`),
    });
}

export function useVendorRanking(mode: 'sales' | 'goals', period: string) {
    return useQuery({
        queryKey: QK.vendorRanking(mode, period),
        queryFn: () => apiFetch<any>(`/api/admin/analytics/vendors/ranking?period=${period}`),
        enabled: mode === 'sales',
    });
}

export function useVendorGoalRanking(
    mode: 'sales' | 'goals',
    selectedMonth: { month: number; year: number } | null
) {
    const params = selectedMonth
        ? `?month=${selectedMonth.month}&year=${selectedMonth.year}`
        : '';
    return useQuery({
        queryKey: QK.vendorGoalRanking(selectedMonth?.month ?? null, selectedMonth?.year ?? null),
        queryFn: () => apiFetch<any>(`/api/admin/analytics/vendors/ranking-by-goals${params}`),
        enabled: mode === 'goals',
    });
}

export function useVendorAvailableMonths(enabled: boolean) {
    return useQuery({
        queryKey: QK.vendorAvailableMonths,
        queryFn: () => apiFetch<{ months: any[] }>('/api/admin/analytics/vendors/ranking-available-months'),
        enabled,
        staleTime: 1000 * 60 * 5,
    });
}

export function useVendorPayments(vendorId: string | null) {
    return useQuery({
        queryKey: QK.vendorPayments(vendorId ?? ''),
        queryFn: () => apiFetch<{ payments: any[] }>(`/api/admin/sales/vendors/${vendorId}/payments`),
        enabled: !!vendorId,
        staleTime: 1000 * 60 * 2,
    });
}

// ─────────────────────────────────────────────────────────────
// REVENUE BY CATEGORY
// ─────────────────────────────────────────────────────────────

export function useRevenueByCategory(period: string) {
    return useQuery({
        queryKey: QK.revenueByCategory(period),
        queryFn: () => apiFetch<any>(`/api/admin/analytics/revenue-by-category?period=${period}`),
    });
}

// ─────────────────────────────────────────────────────────────
// ACCOUNTS & COUPONS
// ─────────────────────────────────────────────────────────────

export function useAccounts() {
    return useQuery({
        queryKey: QK.accounts,
        queryFn: () => apiFetch<{ accounts: any[] }>('/api/admin/accounts'),
    });
}

export function useMyCoupons() {
    return useQuery({
        queryKey: QK.myCoupons,
        queryFn: () => apiFetch<{ coupons: any[] }>('/api/admin/sales/my-coupons'),
    });
}

export function useMyCouponsStats() {
    return useQuery({
        queryKey: QK.myCouponsStats,
        queryFn: () => apiFetch<any>('/api/admin/sales/my-coupons/stats'),
    });
}

export function useAllCoupons() {
    return useQuery({
        queryKey: QK.allCoupons,
        queryFn: () => apiFetch<{ coupons: any[] }>('/api/admin/sales/coupons/all'),
    });
}

export function useCouponsImpact(period: string) {
    return useQuery({
        queryKey: QK.couponsImpact(period),
        queryFn: () => apiFetch<any>(`/api/admin/analytics/coupons-impact?period=${period}`),
    });
}

export function useMarketplaceProducts() {
    return useQuery({
        queryKey: QK.marketplaceProducts,
        queryFn: () => apiFetch<{ products: any[] }>('/api/admin/marketplace/products'),
        staleTime: 1000 * 60 * 5,
    });
}

// ─────────────────────────────────────────────────────────────
// ENGAGEMENT (AdminStats — aba Engajamento Real)
// ─────────────────────────────────────────────────────────────

export function useEngagementStats(enabled = true) {
    return useQuery({
        queryKey: QK.engagementStats,
        queryFn: () => apiFetch<any>('/api/admin/engagement/stats'),
        enabled,
        staleTime: 1000 * 60 * 5,
    });
}

export function useEngagementUsers(enabled = true, page = 1, limit = 30) {
    return useQuery({
        queryKey: QK.engagementUsers(page, limit),
        queryFn: () => apiFetch<{ users: any[]; total: number; page: number; totalPages: number }>(
            `/api/admin/engagement/users?page=${page}&limit=${limit}`
        ),
        enabled,
        staleTime: 1000 * 60 * 5,
    });
}

// ─────────────────────────────────────────────────────────────
// MUTATIONS HELPERS (invalidam o cache certo após ações)
// ─────────────────────────────────────────────────────────────

export function useInvalidate() {
    const qc = useQueryClient();
    return {
        products: () => qc.invalidateQueries({ queryKey: QK.products }),
        accounts: () => qc.invalidateQueries({ queryKey: QK.accounts }),
        myCoupons: () => {
            qc.invalidateQueries({ queryKey: QK.myCoupons });
            qc.invalidateQueries({ queryKey: QK.myCouponsStats });
        },
        allCoupons: () => qc.invalidateQueries({ queryKey: QK.allCoupons }),
        vendorsList: () => qc.invalidateQueries({ queryKey: ['vendorsList'] }),
        vendorRanking: () => qc.invalidateQueries({ queryKey: ['vendorRanking'] }),
        salesHistory: () => qc.invalidateQueries({ queryKey: ['salesHistory'] }),
        subscriptions: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
        profitability: () => {
            qc.invalidateQueries({ queryKey: ['profitabilityStats'] });
            qc.invalidateQueries({ queryKey: ['profitabilityExpenses'] });
            qc.invalidateQueries({ queryKey: ['profitabilityChart'] });
        },
        overviewStats: () => qc.invalidateQueries({ queryKey: QK.overviewStats }),
        vendorPayments: (vendorId: string) =>
            qc.invalidateQueries({ queryKey: QK.vendorPayments(vendorId) }),
        engagementStats: () => qc.invalidateQueries({ queryKey: QK.engagementStats }),
        engagementUsers: () => qc.invalidateQueries({ queryKey: ['engagementUsers'] }),
        platformContentStats: () => qc.invalidateQueries({ queryKey: QK.platformContentStats }),
    };
}